// src/scenes/meal-entry/MealCountsMonthly.jsx
import React, { useState, useEffect } from "react";
import {
    Box, Table, TableHead, TableRow, TableCell, TableBody, TextField,
    Select, MenuItem, FormControl, InputLabel, IconButton, Button,
    Typography, useTheme, CircularProgress, Modal
} from "@mui/material";
import { AddCircleOutline, RemoveCircleOutline } from "@mui/icons-material";
import axios from "axios";

/* ---------------- CONSTANTS ---------------- */
const blankCounts = {
    BR_Free: 0, BR_Reduced: 0, BR_Paid: 0,
    LN_Free: 0, LN_Reduced: 0, LN_Paid: 0,
    SP_Free: 0, SP_Reduced: 0, SP_Paid: 0,
    SN_Free: 0, SN_Reduced: 0, SN_Paid: 0,
};
const initialRow = { ...blankCounts, siteId: "", siteName: "", isHighRate: false };
const mealPrefixMap = { BR: "Breakfast", LN: "Lunch", SP: "Supper", SN: "Snack" };

export default function MealCountsEntryMonthly() {
    const theme = useTheme();
    const { palette: { divider, action, background, text, primary, success, warning, error: errColor, info } } = theme;

    /* ---------- global controls ---------- */
    const [globalDate, setGlobalDate] = useState("");
    const [globalCategory, setGlobalCategory] = useState("");
    const [globalSubcategory, setGlobalSubcategory] = useState("");
    const [categories, setCategories] = useState([]);
    const [subcategories, setSubcategories] = useState([]);

    /* ---------- data & UI state ---------- */
    const [rows, setRows] = useState([{ ...initialRow }]);
    const [schools, setSchools] = useState([]);
    const [rates, setRates] = useState({});   // { Breakfast:{Base:{},High:{}}, … }
    const [loading, setLoading] = useState(true);
    const [fetchError, setFetchError] = useState(null);
    const [error, setError] = useState("");

    /* ---------- progress modal ---------- */
    const [progressOpen, setProgressOpen] = useState(false);
    const [progressMessage, setProgressMessage] = useState("");

    /* ---------- fetch look-ups once ---------- */
    useEffect(() => {
        Promise.all([
            axios.get("https://figazuresql.swancreate.com/api/swa/SchoolMst"),
            axios.get("https://figazuresql.swancreate.com/api/swa/finance_categories"),
            axios.get("https://figazuresql.swancreate.com/api/swa/finance_subcategories"),
            axios.get("https://figazuresql.swancreate.com/api/swa/MealReimbursementRates"),
        ])
            .then(([schRes, catRes, subRes, rateRes]) => {
                /* --- schools with high-rate flag --- */
                const sch = schRes.data
                    .filter(s => s.SchoolId && s.SchoolName)
                    .map(s => ({
                        id: s.SchoolId,
                        name: s.SchoolName,
                        // bit may come as 0/1, "0"/"1", or boolean
                        isHighRate: Boolean(Number(s.isHighRate ?? s.IsHighRate ?? 0))
                    }))
                    .sort((a, b) => a.name.localeCompare(b.name));
                setSchools(sch);

                /* --- categories / subcategories --- */
                setCategories(catRes.data.filter(c => c.Type === "Income"));
                setSubcategories(subRes.data);

                /* --- rates: build Base & High buckets --- */
                const grouped = {};
                rateRes.data.forEach(r => {
                    if (!grouped[r.MealService]) grouped[r.MealService] = { Base: {}, High: {} };
                    const status = r.PaymentStatus;                // Free / Reduced / Paid
                    grouped[r.MealService].Base[status] = parseFloat(r.Rate ?? 0);
                    grouped[r.MealService].High[status] = parseFloat(
                        r.RateHigh ?? r.HighRate ?? r.Rate ?? 0      // handle either column name
                    );
                });
                setRates(grouped);
            })
            .catch(err => {
                console.error("Lookup load error:", err);
                setFetchError("Failed to load look-ups. Please try again later.");
            })
            .finally(() => setLoading(false));
    }, []);

    /* ---------- handle cell edits ---------- */
    const handleCellChange = (rowIdx, field, value) => {
        const copy = [...rows];
        if (field === "siteId") {
            const sel = schools.find(s => s.id === value) || {};
            copy[rowIdx].siteId = value;
            copy[rowIdx].siteName = sel.name || "";
            copy[rowIdx].isHighRate = sel.isHighRate;          // keep flag
        } else {
            copy[rowIdx][field] = Number(value) || 0;
        }
        setRows(copy);
    };

    /* ---------- $ helpers ---------- */
    const mealTotalMoney = (row, prefix) => {
        const tier = row.isHighRate ? "High" : "Base";
        const svcRates = (rates[mealPrefixMap[prefix]] || {})[tier] || {};
        return (
            row[`${prefix}_Free`] * (svcRates.Free || 0) +
            row[`${prefix}_Reduced`] * (svcRates.Reduced || 0) +
            row[`${prefix}_Paid`] * (svcRates.Paid || 0)
        );
    };
    const rowTotalMoney = row =>
        Object.keys(mealPrefixMap).reduce((sum, p) => sum + mealTotalMoney(row, p), 0);

    /* ---------- add / remove site rows ---------- */
    const addRow = () => setRows([...rows, { ...initialRow }]);
    const removeRow = (i) => setRows(rows.filter((_, idx) => idx !== i));

    /* ---------- submit counts + income ---------- */
    const handleSubmit = async () => {
        if (!globalCategory) return setError("Please select a Category (Income).");
        if (!globalSubcategory) return setError("Please select a Subcategory.");
        if (!globalDate) return setError("Please select a Date.");
        if (!rows.length) return setError("Add at least one site.");
        setError(""); setProgressOpen(true);

        try {
            /* 1) meal counts */
            for (let i = 0; i < rows.length; i++) {
                const r = rows[i];
                setProgressMessage(`Exporting meal counts for ${r.siteName} (${i + 1}/${rows.length})…`);
                await axios.post("https://figazuresql.swancreate.com/api/swa/FINANCE_MealCounts", {
                    MonthDate: globalDate,
                    Date: globalDate,
                    SchoolId: r.siteId,
                    SchoolName: r.siteName,
                    ...Object.fromEntries(Object.keys(blankCounts).map(k => [k, r[k]])),
                    CountType: "Monthly",
                    BR_Total: mealTotalMoney(r, "BR"),
                    LN_Total: mealTotalMoney(r, "LN"),
                    SP_Total: mealTotalMoney(r, "SP"),
                    SN_Total: mealTotalMoney(r, "SN"),
                    Summary_Total: rowTotalMoney(r),
                });
            }

            /* 2) income */
            const catName = categories.find(c => c.CategoryID === globalCategory)?.CategoryName || "";
            const subName = subcategories.find(sc => sc.SubcategoryID === globalSubcategory)?.SubcategoryName || "";
            for (let i = 0; i < rows.length; i++) {
                const r = rows[i];
                setProgressMessage(`Exporting income for ${r.siteName} (${i + 1}/${rows.length})…`);
                await axios.post("https://figazuresql.swancreate.com/api/swa/FINANCE_Incomes", {
                    MonthDate: globalDate,
                    IncomeDate: globalDate,
                    SchoolId: r.siteId,
                    SiteName: r.siteName,
                    IncomeCategoryID: globalCategory,
                    IncomeCategory: catName,
                    IncomeSubCategoryID: globalSubcategory,
                    IncomeSubcategory: subName,
                    Amount: rowTotalMoney(r),
                    Description: "",
                });
            }

            /* reset form */
            setRows([{ ...initialRow }]);
            setGlobalDate(""); setGlobalCategory(""); setGlobalSubcategory("");
        } catch (err) {
            console.error("Export error:", err);
            setError("Export failed. See console.");
        } finally {
            setProgressOpen(false);
        }
    };

    /* ---------- render ---------- */
    if (loading) {
        return (
            <Box mt={4} display="flex" justifyContent="center"><CircularProgress /></Box>
        );
    }

    return (
        <Box p={3}>
            <Typography variant="h4" gutterBottom>
                FIG Meal Counts by Date / Site
            </Typography>

            {fetchError && <Typography color="error">{fetchError}</Typography>}

            {/* global filters */}
            <Box display="flex" gap={2} mb={3}>
                <FormControl size="small">
                    <InputLabel>Date</InputLabel>
                    <TextField type="date" value={globalDate} onChange={e => setGlobalDate(e.target.value)} size="small" />
                </FormControl>
                <FormControl size="small">
                    <InputLabel>Category</InputLabel>
                    <Select value={globalCategory} onChange={e => setGlobalCategory(e.target.value)} size="small" label="Category">
                        <MenuItem value="">—</MenuItem>
                        {categories.map(c => <MenuItem key={c.CategoryID} value={c.CategoryID}>{c.CategoryName}</MenuItem>)}
                    </Select>
                </FormControl>
                <FormControl size="small">
                    <InputLabel>Subcategory</InputLabel>
                    <Select
                        value={globalSubcategory}
                        onChange={e => setGlobalSubcategory(e.target.value)}
                        size="small"
                        label="Subcategory"
                        disabled={!globalCategory}
                    >
                        <MenuItem value="">—</MenuItem>
                        {subcategories.filter(sc => sc.CategoryID === globalCategory)
                            .map(sc => <MenuItem key={sc.SubcategoryID} value={sc.SubcategoryID}>{sc.SubcategoryName}</MenuItem>)}
                    </Select>
                </FormControl>
            </Box>

            {/* entry table */}
            <Table size="small" sx={{ border: `1px solid ${divider}` }}>
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ backgroundColor: primary.main, color: "#fff", fontWeight: "bold" }}>Site</TableCell>
                        {Object.entries(mealPrefixMap).map(([p, meal]) => (
                            <React.Fragment key={p}>
                                <TableCell align="center" sx={{
                                    backgroundColor: meal === "Breakfast" ? success.main : meal === "Lunch" ? info.main
                                        : meal === "Supper" ? warning.main : errColor.main,
                                    color: "#fff", fontWeight: "bold"
                                }}>
                                    {meal}
                                    <Box display="flex" justifyContent="space-between" pt={1}>
                                        <Typography>F</Typography><Typography>R</Typography><Typography>P</Typography>
                                    </Box>
                                </TableCell>
                                <TableCell align="center" sx={{
                                    backgroundColor: meal === "Breakfast" ? success.main : meal === "Lunch" ? info.main
                                        : meal === "Supper" ? warning.main : errColor.main, color: "#fff"
                                }}>$</TableCell>
                            </React.Fragment>
                        ))}
                        <TableCell sx={{ backgroundColor: primary.main, color: "#fff", fontWeight: "bold" }}>Total $</TableCell>
                        <TableCell sx={{ backgroundColor: primary.main }} />
                    </TableRow>
                </TableHead>

                <TableBody>
                    {rows.map((r, idx) => {
                        const usedIds = rows.filter((_, i) => i !== idx).map(x => x.siteId);
                        const selectable = schools.filter(s => !usedIds.includes(s.id) || s.id === r.siteId);
                        return (
                            <TableRow key={idx} sx={{ backgroundColor: idx % 2 ? action.hover : background.paper }}>
                                {/* site selector */}
                                <TableCell>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Site</InputLabel>
                                        <Select
                                            value={r.siteId}
                                            label="Site"
                                            onChange={e => handleCellChange(idx, "siteId", e.target.value)}
                                            size="small"
                                        >
                                            {selectable.map(s => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
                                        </Select>
                                    </FormControl>
                                </TableCell>

                                {/* meal counts + $ */}
                                {Object.keys(mealPrefixMap).map(p => (
                                    <React.Fragment key={p}>
                                        <TableCell sx={{ borderLeft: `3px solid ${p === "BR" ? success.main : p === "LN" ? info.main : p === "SP" ? warning.main : errColor.main}` }}>
                                            <Box display="flex" justifyContent="space-between" gap={1}>
                                                {["Free", "Reduced", "Paid"].map(stat => (
                                                    <TextField
                                                        key={stat}
                                                        type="number"
                                                        value={r[`${p}_${stat}`]}
                                                        onChange={e => handleCellChange(idx, `${p}_${stat}`, e.target.value)}
                                                        size="small"
                                                        sx={{
                                                            width: 60, backgroundColor: background.paper,
                                                            "& input[type=number]": { MozAppearance: "textfield" },
                                                            "& input::-webkit-outer-spin-button,& input::-webkit-inner-spin-button": { WebkitAppearance: "none", margin: 0 },
                                                            "& .MuiOutlinedInput-notchedOutline": { borderColor: divider }
                                                        }}
                                                    />
                                                ))}
                                            </Box>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography color="text.secondary">
                                                {mealTotalMoney(r, p).toLocaleString(undefined, { style: "currency", currency: "USD" })}
                                            </Typography>
                                        </TableCell>
                                    </React.Fragment>
                                ))}

                                {/* total + actions */}
                                <TableCell>
                                    <Typography color="text.secondary">
                                        {rowTotalMoney(r).toLocaleString(undefined, { style: "currency", currency: "USD" })}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <IconButton onClick={() => removeRow(idx)} sx={{ color: text.primary }}>
                                        <RemoveCircleOutline />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        );
                    })}

                    {/* add row */}
                    <TableRow>
                        <TableCell colSpan={10} align="center">
                            <IconButton onClick={addRow} sx={{ color: text.primary }}>
                                <AddCircleOutline /> Add Site
                            </IconButton>
                        </TableCell>
                        <TableCell />
                    </TableRow>
                </TableBody>
            </Table>

            {error && <Typography color="error" mt={2}>{error}</Typography>}

            {/* submit + summary */}
            <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
                <Button variant="contained" onClick={handleSubmit}>Submit</Button>
                <Typography>
                    Summary Total:&nbsp;
                    <Typography component="span" color="text.secondary" fontWeight="bold">
                        {rows.reduce((s, r) => s + rowTotalMoney(r), 0).toLocaleString(undefined, { style: "currency", currency: "USD" })}
                    </Typography>
                </Typography>
            </Box>

            {/* progress modal */}
            <Modal open={progressOpen} sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                <Box sx={{ backgroundColor: background.paper, p: 3, borderRadius: 2, boxShadow: 24, textAlign: "center" }}>
                    <CircularProgress />
                    <Typography mt={2}>{progressMessage || "Processing…"}</Typography>
                </Box>
            </Modal>
        </Box>
    );
}
